/**
 * useAIIntake — manages the AI intake conversation session.
 *
 * Handles:
 *   - Starting a session via POST /api/ai/intake/start
 *   - Sending messages and reading the SSE stream from POST /api/ai/intake/message
 *   - Parsing field emission events and updating collectedFields
 *   - Detecting the <complete /> signal and transitioning to confirmation
 *   - Reconnection on network error
 *   - Partial progress persistence via sessionStorage (survives soft refresh)
 *   - Finalising via POST /api/ai/intake/complete
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { getAuthToken, API_BASE } from '../lib/api.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export interface AIIntakeHookOptions {
  formId: string;
  assignmentId?: string | undefined;
  locationId: string;
  contactName?: string | undefined;
  /** Contact JWT (assigned-form path). If omitted, falls back to the in-memory token. */
  authToken?: string | undefined;
  /** Short-lived public form grant (public-form path, unauthenticated visitor). */
  grant?: string | undefined;
  /**
   * Form-package step id. When set, /start pins the AI session to this run item
   * server-side and /complete short-circuits (no submission insert) — the caller
   * forwards the returned formData to POST /forms/submit with this same id.
   */
  packageRunItemId?: string | undefined;
  /**
   * Short-lived run grant (public-package path). Sent as X-Run-Grant so a
   * token-less /pkg visitor can drive the AI session for their run.
   */
  runGrant?: string | undefined;
}

/** Result of /complete: a submission (single-form) OR deferred package data. */
export interface ConfirmResult {
  submissionId?: string;
  /** True for package sessions — no submission was inserted; forward formData. */
  deferred?: boolean;
  packageRunItemId?: string;
  formData?: Record<string, unknown>;
}

export interface AIIntakeHook {
  messages: ChatMessage[];
  collectedFields: Record<string, unknown>;
  isComplete: boolean;
  isTyping: boolean;
  isStarting: boolean;
  error: string | null;
  sessionId: string | null;
  collectedCount: number;
  totalRequired: number;
  missingRequired: string[];
  signatureRequired: boolean;
  ssnRequired: boolean;
  startSession: () => Promise<void>;
  sendMessage: (text: string) => void;
  confirmSubmit: (confirmedData: Record<string, unknown>, signature?: string, ssn?: string) => Promise<ConfirmResult | null>;
  dismissError: () => void;
}

// ---------------------------------------------------------------------------
// Session storage key
// ---------------------------------------------------------------------------

function storageKey(formId: string, assignmentId?: string): string {
  return `ci_intake_${formId}_${assignmentId ?? 'public'}`;
}

interface PersistedState {
  sessionId: string;
  messages: Array<{ id: string; role: 'user' | 'assistant'; content: string; ts: string }>;
  collectedFields: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAIIntake({
  formId,
  assignmentId,
  locationId,
  contactName,
  authToken,
  grant,
  packageRunItemId,
  runGrant,
}: AIIntakeHookOptions): AIIntakeHook {
  const [messages,        setMessages]        = useState<ChatMessage[]>([]);
  const [collectedFields, setCollectedFields] = useState<Record<string, unknown>>({});
  const [isComplete,      setIsComplete]      = useState(false);
  const [isTyping,        setIsTyping]        = useState(false);
  const [isStarting,      setIsStarting]      = useState(false);
  const [error,           setError]           = useState<string | null>(null);
  const [sessionId,       setSessionId]       = useState<string | null>(null);
  const [collectedCount,  setCollectedCount]  = useState(0);
  const [totalRequired,   setTotalRequired]   = useState(0);
  const [missingRequired, setMissingRequired] = useState<string[]>([]);

  const abortRef = useRef<AbortController | null>(null);

  // ---------------------------------------------------------------------------
  // Restore from sessionStorage on mount
  // ---------------------------------------------------------------------------

  useEffect(() => {
    try {
      const key = storageKey(formId, assignmentId);
      const raw = sessionStorage.getItem(key);
      if (raw) {
        const saved = JSON.parse(raw) as PersistedState;
        setSessionId(saved.sessionId);
        setMessages(
          saved.messages.map((m) => ({
            id:        m.id,
            role:      m.role,
            content:   m.content,
            timestamp: new Date(m.ts),
          })),
        );
        setCollectedFields(saved.collectedFields ?? {});
      }
    } catch {
      // ignore — sessionStorage may be unavailable
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------------------------------------------------
  // Persist to sessionStorage on changes
  // ---------------------------------------------------------------------------

  const persistState = useCallback((
    sid: string,
    msgs: ChatMessage[],
    fields: Record<string, unknown>,
  ) => {
    try {
      const key = storageKey(formId, assignmentId);
      const state: PersistedState = {
        sessionId: sid,
        messages:  msgs.map((m) => ({ id: m.id, role: m.role, content: m.content, ts: m.timestamp.toISOString() })),
        collectedFields: fields,
      };
      sessionStorage.setItem(key, JSON.stringify(state));
    } catch {
      // ignore
    }
  }, [formId, assignmentId]);

  // ---------------------------------------------------------------------------
  // Auth header helper
  // ---------------------------------------------------------------------------

  function buildHeaders(): Record<string, string> {
    const h: Record<string, string> = { 'Content-Type': 'application/json' };
    // Prefer an explicitly-passed contact JWT, otherwise fall back to the
    // in-memory bearer token held by the api client (assigned-form path was
    // previously sending NO Authorization header).
    const bearer = authToken ?? getAuthToken();
    if (bearer) h['Authorization'] = `Bearer ${bearer}`;
    // Public-form visitors carry a short-lived form grant instead of a JWT.
    if (grant) h['X-Form-Grant'] = grant;
    // Public-package visitors carry a short-lived run grant instead of a JWT.
    if (runGrant) h['X-Run-Grant'] = runGrant;
    return h;
  }

  // ---------------------------------------------------------------------------
  // Start session
  // ---------------------------------------------------------------------------

  const startSession = useCallback(async () => {
    if (sessionId) return;  // already started (or restored from storage)
    setIsStarting(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/ai/intake/start`, {
        method:  'POST',
        headers: buildHeaders(),
        body: JSON.stringify({ formId, assignmentId, packageRunItemId, locationId, contactName }),
      });

      if (!res.ok) {
        const err = await res.json() as { error?: string };
        throw new Error(err.error ?? 'Failed to start intake session');
      }

      const data = await res.json() as {
        success: boolean;
        data?: { sessionId: string; greeting: string; formName: string; totalRequired: number };
        error?: string;
      };

      if (!data.success || !data.data) {
        throw new Error(data.error ?? 'Failed to start intake session');
      }

      const { sessionId: sid, greeting, totalRequired: tot } = data.data;
      const greetingMsg: ChatMessage = {
        id:        `ai-${Date.now()}`,
        role:      'assistant',
        content:   greeting,
        timestamp: new Date(),
      };

      setSessionId(sid);
      setTotalRequired(tot);
      setMessages([greetingMsg]);
      persistState(sid, [greetingMsg], {});
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start session');
    } finally {
      setIsStarting(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, formId, assignmentId, packageRunItemId, locationId, contactName, authToken, grant, runGrant]);

  // ---------------------------------------------------------------------------
  // Send message — SSE stream
  // ---------------------------------------------------------------------------

  const sendMessage = useCallback((text: string) => {
    if (!sessionId || !text.trim() || isTyping) return;

    // Optimistically add user message
    const userMsg: ChatMessage = {
      id:        `user-${Date.now()}`,
      role:      'user',
      content:   text.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => {
      const updated = [...prev, userMsg];
      persistState(sessionId, updated, collectedFields);
      return updated;
    });
    setIsTyping(true);
    setError(null);

    // Cancel any in-flight stream
    abortRef.current?.abort();
    abortRef.current = new AbortController();

    // Accumulate streaming text
    let streamingContent = '';
    const assistantId = `ai-${Date.now() + 1}`;

    // Append placeholder assistant message
    setMessages((prev) => [
      ...prev,
      { id: assistantId, role: 'assistant', content: '', timestamp: new Date() },
    ]);

    fetch(`${API_BASE}/api/ai/intake/message`, {
      method:  'POST',
      headers: buildHeaders(),
      body:    JSON.stringify({ sessionId, message: text.trim() }),
      signal:  abortRef.current.signal,
    })
      .then(async (res) => {
        if (!res.ok || !res.body) {
          throw new Error('Stream failed');
        }

        const reader = res.body.getReader();
        const dec    = new TextDecoder();
        let buffer   = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += dec.decode(value, { stream: true });

          // Process complete SSE lines
          const lines = buffer.split('\n\n');
          buffer = lines.pop() ?? '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            const raw = line.slice(6).trim();
            if (!raw) continue;

            try {
              const event = JSON.parse(raw) as {
                type: string;
                content?: string;
                key?: string;
                value?: unknown;
                allFieldsCollected?: boolean;
                collectedCount?: number;
                totalRequired?: number;
                missingRequired?: string[];
                sessionComplete?: boolean;
                message?: string;
              };

              switch (event.type) {
                case 'text':
                  streamingContent += event.content ?? '';
                  setMessages((prev) =>
                    prev.map((m) =>
                      m.id === assistantId ? { ...m, content: streamingContent } : m,
                    ),
                  );
                  break;

                case 'field':
                  if (event.key !== undefined) {
                    setCollectedFields((prev) => {
                      const updated = { ...prev, [event.key as string]: event.value };
                      return updated;
                    });
                  }
                  break;

                case 'done':
                  setCollectedCount(event.collectedCount ?? 0);
                  setTotalRequired(event.totalRequired ?? 0);
                  setMissingRequired(event.missingRequired ?? []);
                  if (event.sessionComplete) setIsComplete(true);
                  break;

                case 'error':
                  setError(event.message ?? 'AI service error');
                  break;
              }
            } catch {
              // ignore parse errors
            }
          }
        }

        // Persist final state
        setMessages((prev) => {
          persistState(sessionId, prev, collectedFields);
          return prev;
        });
      })
      .catch((err: Error) => {
        if (err.name === 'AbortError') return;
        setError('Connection lost — your progress is saved. Please try again.');
        // Remove placeholder assistant message on error
        setMessages((prev) => prev.filter((m) => m.id !== assistantId));
      })
      .finally(() => {
        setIsTyping(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, isTyping, collectedFields, authToken, grant, runGrant]);

  // ---------------------------------------------------------------------------
  // Confirm and submit
  // ---------------------------------------------------------------------------

  const confirmSubmit = useCallback(async (
    confirmedData: Record<string, unknown>,
    signature?: string,
    ssn?: string,
  ): Promise<ConfirmResult | null> => {
    if (!sessionId) return null;
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/ai/intake/complete`, {
        method:  'POST',
        headers: buildHeaders(),
        body:    JSON.stringify({ sessionId, confirmedData, signature, ssn }),
      });

      const data = await res.json() as {
        success: boolean;
        // Single-form sessions return { submissionId }; package steps short-circuit
        // with { deferred, packageRunItemId, formData } (no submission inserted).
        data?: ConfirmResult;
        error?: string;
      };

      if (!data.success || !data.data) {
        throw new Error(data.error ?? 'Submission failed');
      }

      // Clear persisted state
      try {
        sessionStorage.removeItem(storageKey(formId, assignmentId));
      } catch {
        // ignore
      }

      return data.data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Submission failed');
      return null;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, authToken, grant, runGrant, formId, assignmentId]);

  // ---------------------------------------------------------------------------
  // Derived state
  // ---------------------------------------------------------------------------

  const signatureRequired = Boolean(collectedFields['signatureRequired']);
  const ssnRequired       = Boolean(collectedFields['ssnRequired']);

  return {
    messages,
    collectedFields,
    isComplete,
    isTyping,
    isStarting,
    error,
    sessionId,
    collectedCount,
    totalRequired,
    missingRequired,
    signatureRequired,
    ssnRequired,
    startSession,
    sendMessage,
    confirmSubmit,
    dismissError: () => setError(null),
  };
}
